import { Injectable } from '@angular/core';
import { RocosSdkClientService } from '@shared/services/rocos-sdk-client';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';

export interface AgentLogResult {
  available: boolean;
  content: string;
}

const DEFAULT_TIMEOUT_MS = 15000;
const JOURNALCTL_MAX_LINES = 2000;
const JOURNALCTL_MAX_BYTES = 400000;

/**
 * Fetches recent rocos-agent journal lines via the agent's /shell/run callable. The agent has
 * no logs callable of its own — journald is the only history (see plan doc for the trail).
 *
 * Failure is always non-fatal: robots are frequently offline, which is often the reason someone
 * is exporting debug info, so a failed/timed-out fetch here must never block the rest of the
 * bundle.
 */
@Injectable({
  providedIn: 'root',
})
export class AgentLogService {
  constructor(private rocosSdkClientService: RocosSdkClientService) {}

  async fetch(
    projectId: string,
    callsign: string,
    sinceUtc: Date,
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ): Promise<AgentLogResult> {
    const cmd = this.buildJournalctlCommand(Math.floor(sinceUtc.getTime() / 1000));

    try {
      const { return$ } = this.rocosSdkClientService.client.getCallerService().call({
        projectId,
        callsign,
        source: '/shell/run',
        payload: { cmd },
      });

      const content = await firstValueFrom(return$.pipe(timeout(timeoutMs)));

      return { available: true, content: String(content) };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return { available: false, content: `Agent log unavailable: ${message}` };
    }
  }

  // Exposed for testing. Must contain no double quotes (the agent wraps this in
  // `bash -c "cd / ; ${$cmd}"` and shlex-splits the result) and cap its own output, since the
  // agent does an unbounded io.ReadAll on stdout. --quiet suppresses journalctl's own "Hint:
  // you are currently not seeing messages from other users" notice — without it, that line
  // rides the 2>&1 redirect into every single bundle's agent-journal.log.
  buildJournalctlCommand(epochSeconds: number): string {
    return (
      `journalctl --unit=rocos-agent --since=@${epochSeconds} --utc --output=short-iso-precise ` +
      `--no-pager --quiet --lines=${JOURNALCTL_MAX_LINES} 2>&1 | tail -c ${JOURNALCTL_MAX_BYTES}`
    );
  }
}
